import { Show, SignOutButton, useUser } from '@clerk/react-router';
import { ArrowLeftStartOnRectangleIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useLoadedProvider } from '~/features/vault/account/vaultSelectors';
import { paths } from '~/routing';
import { DownloadMyDataLink } from './DownloadMyDataLink';
import { VaultHlxSwitchLink } from './VaultHlxSwitchLink';
import { SECTION_ICON_MAP } from './constants';

interface IconRailSection {
  label: string;
  subMenu: { pageKey: string }[];
}

interface IconRailProps {
  sections: IconRailSection[];
  activeSection: string;
  onSectionClick: (section: string, anchorRect: DOMRect) => void;
  onExpand: () => void;
  /** Role-aware destination on HLX when cross-brand navigation is available. */
  switchTargetPath?: string;
  allowBrandSwitch?: boolean;
  allowSignOut?: boolean;
}

export const IconRail = ({
  sections,
  activeSection,
  onSectionClick,
  onExpand,
  switchTargetPath,
  allowBrandSwitch = true,
  allowSignOut = true,
}: IconRailProps) => {
  const { user } = useUser();
  const { provider } = useLoadedProvider();
  const initials = provider
    ? ((provider.firstName?.[0] || '') + (provider.lastName?.[0] || '')).toUpperCase() || '?'
    : '?';

  return (
    <nav
      aria-label="Collapsed sidebar navigation"
      className="w-14 h-full bg-primary-t-900 border-r border-primary-t-700 py-3 flex flex-col items-center gap-0.5"
    >
      <button
        type="button"
        aria-label="Expand sidebar"
        onClick={onExpand}
        className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer bg-white/50 hover:bg-white/80 transition-colors duration-150 mb-2"
      >
        <ChevronDoubleRightIcon className="size-4 text-grey-400" />
      </button>
      {user?.hasImage ? (
        <img
          src={user.imageUrl}
          alt="Profile"
          className="w-8 h-8 rounded-full object-cover ring-1 ring-white/20 mb-2 shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-50 to-primary-300 flex items-center justify-center text-white text-[11px] font-semibold ring-1 ring-white/20 mb-2 shrink-0">
          {initials}
        </div>
      )}
      <>
        {allowBrandSwitch && <VaultHlxSwitchLink compact switchTargetPath={switchTargetPath} />}
        <div className="w-9 h-px bg-primary-t-700 mb-2" />
      </>

      {/* Section icons */}
      {sections.map((section) => {
        const Icon = SECTION_ICON_MAP[section.label];
        const isActive = section.label === activeSection;
        if (!Icon) return null;
        return (
          <button
            key={section.label}
            type="button"
            aria-label={section.label}
            onClick={(e) => onSectionClick(section.label, e.currentTarget.getBoundingClientRect())}
            className={`w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer transition-colors duration-150 my-px ${
              isActive ? 'bg-white shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Icon className={`size-[18px] ${isActive ? 'text-primary-50' : 'text-grey-400'}`} />
          </button>
        );
      })}

      <div className="mt-auto pt-2">
        {provider?.id != null && <DownloadMyDataLink providerId={provider.id} compact />}
        {allowSignOut && (
          <Show when="signed-in">
            <SignOutButton redirectUrl={paths.landing}>
              <button
                type="button"
                data-cy={CT.Homepage.signoutBtn}
                aria-label="Sign out"
                title="Sign out"
                className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/50 transition-colors duration-150"
              >
                <ArrowLeftStartOnRectangleIcon className="size-4 text-grey-400" />
              </button>
            </SignOutButton>
          </Show>
        )}
      </div>
    </nav>
  );
};
